import { useEffect, useRef } from "react";

/**
 * Two signals within this many milliseconds are one return: coming back to a
 * window usually fires both `focus` and `visibilitychange`.
 */
export const RETURN_DEDUPE_MS = 1000;

/**
 * Call `onReturn` when the person comes back to this window: the window
 * regains focus, or the page becomes visible again (a browser tab brought
 * back to the front). Both signals of one return call it once.
 *
 * Use it to re-read what may have changed elsewhere while the person was
 * away, such as Story Points typed in ADO during Present mode (UX-PM-05).
 * React Query's own focus refetch skips data younger than the app-wide
 * `staleTime` (60 s), so a read that must follow every return asks here.
 *
 * Nothing is called while `enabled` is false. The latest `onReturn` is always
 * used, so it needs no memoising.
 */
export function useRefetchOnReturn(onReturn: () => void, enabled = true): void {
  const latest = useRef(onReturn);
  useEffect(() => {
    latest.current = onReturn;
  }, [onReturn]);

  useEffect(() => {
    if (!enabled) return undefined;
    let lastCall = Number.NEGATIVE_INFINITY;
    const returned = () => {
      const now = Date.now();
      if (now - lastCall < RETURN_DEDUPE_MS) return;
      lastCall = now;
      latest.current();
    };
    const onFocus = () => {
      returned();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") returned();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled]);
}
